import React, { useState, useEffect } from 'react';
import { Button, Badge, Select, Alert, Loader, Tabs } from './shared';
import GitHubTokenManager from './GitHubTokenManager';

const GitHubBrowser = ({ service, isOnline }) => {
    const [activeTab, setActiveTab] = useState('repositories');
    const [repositories, setRepositories] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [selectedProjects, setSelectedProjects] = useState({});
    const [assigning, setAssigning] = useState({});
    const [deleteModal, setDeleteModal] = useState({ show: false, repo: null });
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [deleting, setDeleting] = useState(false);

    // API endpoints
    const TESTING_API = window.location.protocol === 'https:' ? '/api/testing' : `http://${window.location.hostname}:8011`;
    const PROJECTS_API = window.location.protocol === 'https:' ? '/api/projects' : `http://${window.location.hostname}:8010`;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadRepositories();
        loadProjects();
    }, []);

    const loadRepositories = async () => {
        setLoading(true);
        setError(null);

        try {
            // Load only Moreas's repositories
            const response = await fetch(`${TESTING_API}/github/repositories?include_own=true&user_only=true`);

            if (!response.ok) {
                throw new Error(`Failed to load repositories: ${response.status}`);
            }

            const data = await response.json();
            // Filter to only show repos owned by Moreas
            const moreasRepos = (data.repositories || []).filter(repo =>
                repo.owner?.login === 'Moreas' || repo.full_name?.startsWith('Moreas/')
            );
            setRepositories(moreasRepos);
        } catch (err) {
            console.error('Failed to load repositories:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadProjects = async () => {
        try {
            const response = await fetch(`${PROJECTS_API}/projects`);
            if (response.ok) {
                const projectsList = await response.json();

                // Fetch full details for each project to get sub_projects
                const projectsWithDetails = await Promise.all(
                    projectsList.map(async (project) => {
                        try {
                            const detailResponse = await fetch(`${PROJECTS_API}/projects/${project.id}`);
                            if (detailResponse.ok) {
                                return await detailResponse.json();
                            }
                            return project;
                        } catch (err) {
                            console.error(`Failed to load project ${project.id}:`, err);
                            return project;
                        }
                    })
                );

                setProjects(projectsWithDetails);
                console.log('Loaded projects with sub-projects:', projectsWithDetails.length);
            }
        } catch (err) {
            console.error('Failed to load projects:', err);
        }
    };

    const assignToProject = async (repoFullName) => {
        const projectId = selectedProjects[repoFullName];

        if (!projectId) {
            setError('Please select a project first');
            return;
        }

        setAssigning({ ...assigning, [repoFullName]: true });
        setError(null);
        setSuccess(null);

        try {
            console.log('Assigning repository:', repoFullName, 'to project:', projectId);

            // Fetch the latest project data to check for existing sub-projects
            const projectResponse = await fetch(`${PROJECTS_API}/projects/${projectId}`);
            if (!projectResponse.ok) {
                throw new Error(`Failed to load project details: ${projectResponse.status}`);
            }
            const project = await projectResponse.json();
            console.log('Project loaded:', project.name, 'with', project.sub_projects?.length || 0, 'sub-projects');

            const repoUrl = `https://github.com/${repoFullName}`;
            const repoName = repoFullName.split('/')[1];

            // Check if repo already exists in sub_projects
            const repoExists = project.sub_projects?.some(sp => sp.repository_url === repoUrl);

            if (repoExists) {
                const errorMsg = `⚠️ Repository "${repoFullName}" is already assigned to project "${project.name}"`;
                console.warn(errorMsg);
                setError(errorMsg);
                setAssigning({ ...assigning, [repoFullName]: false });
                return;
            }

            // Find repository in current repositories list (avoid re-fetching)
            const repo = repositories.find(r => r.full_name === repoFullName);
            console.log('Repository data:', repo);

            // Add repository as sub-project
            const subProjectData = {
                name: repoName,
                repository_url: repoUrl,
                repository_name: repoFullName,
                sub_project_type: 'backend_api', // Default type
                technology_stack: repo?.language ? [repo.language] : [],
                framework: repo?.topics?.[0] || '',
                language: repo?.language || ''
            };

            console.log('Creating sub-project with data:', subProjectData);

            const response = await fetch(`${PROJECTS_API}/projects/${projectId}/sub-projects`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(subProjectData)
            });

            if (!response.ok) {
                let errorMessage = `Failed to assign repository (${response.status})`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail || errorMessage;
                } catch {
                    const errorText = await response.text();
                    errorMessage = errorText || response.statusText || errorMessage;
                }
                console.error('Assignment failed:', errorMessage);
                throw new Error(errorMessage);
            }

            const result = await response.json();
            console.log('Assignment successful:', result);

            // Refresh projects list to update assignment badges
            await loadProjects();

            const successMsg = `✅ Repository "${repoName}" successfully assigned to project "${project.name}"`;
            console.log('Success message:', successMsg);
            setSuccess(successMsg);

        } catch (err) {
            console.error('Failed to assign repository:', err);
            const errorMsg = `❌ ${err.message || 'An unknown error occurred'}`;
            setError(errorMsg);
        } finally {
            setAssigning({ ...assigning, [repoFullName]: false });
        }
    };

    const deleteRepository = async () => {
        if (deleteConfirmation !== 'DELETE') {
            setError('Please type DELETE to confirm');
            return;
        }

        setDeleting(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await fetch(
                `${TESTING_API}/github/repositories/${encodeURIComponent(deleteModal.repo.full_name)}`,
                {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        confirm_delete: true,
                        typed_confirmation: 'DELETE'
                    })
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to delete repository');
            }

            const successMsg = `✅ Repository "${deleteModal.repo.full_name}" has been permanently deleted from GitHub`;
            console.log('Delete success:', successMsg);
            setSuccess(successMsg);
            setDeleteModal({ show: false, repo: null });
            setDeleteConfirmation('');

            // Refresh repositories list
            await loadRepositories();

        } catch (err) {
            console.error('Failed to delete repository:', err);

            // Check if it's a permissions error
            if (err.message && err.message.includes('delete_repo')) {
                setError('❌ Your GitHub token lacks delete_repo permission. Please reconnect via OAuth in the Token Manager tab to get the required permissions.');
            } else if (err.message && err.message.includes('Must have admin rights')) {
                setError('❌ Repository deletion requires delete_repo permission. Please reconnect via OAuth in the Token Manager tab.');
            } else {
                setError(`❌ ${err.message}`);
            }
            // Don't close modal on error so user can see the message
        } finally {
            setDeleting(false);
        }
    };

    const getAssignedProject = (repoFullName) => {
        const repoUrl = `https://github.com/${repoFullName}`;
        const assignedProject = projects.find(project =>
            project.sub_projects?.some(sp => sp.repository_url === repoUrl)
        );
        return assignedProject;
    };

    const ProjectTypeIcon = ({ language }) => {
        const icons = {
            javascript: <i className="fab fa-js" style={{ marginRight: '8px', color: '#000000' }}></i>,
            typescript: <i className="fab fa-js" style={{ marginRight: '8px', color: '#000000' }}></i>,
            python: <i className="fab fa-python" style={{ marginRight: '8px', color: '#000000' }}></i>,
            java: <i className="fab fa-java" style={{ marginRight: '8px', color: '#000000' }}></i>,
            go: <i className="fab fa-golang" style={{ marginRight: '8px', color: '#000000' }}></i>,
            rust: <i className="fab fa-rust" style={{ marginRight: '8px', color: '#000000' }}></i>,
            php: <i className="fab fa-php" style={{ marginRight: '8px', color: '#000000' }}></i>,
            ruby: <i className="fab fa-gem" style={{ marginRight: '8px', color: '#000000' }}></i>,
            html: <i className="fab fa-html5" style={{ marginRight: '8px', color: '#000000' }}></i>,
            css: <i className="fab fa-css3" style={{ marginRight: '8px', color: '#000000' }}></i>
        };
        return icons[language?.toLowerCase()] || <i className="fas fa-code" style={{ marginRight: '8px', color: '#000000' }}></i>;
    };

    const tabs = [
        { id: 'repositories', label: 'Repositories', icon: 'fa-folder' },
        { id: 'tokens', label: 'Token Manager', icon: 'fa-key' }
    ];

    return (
        <div className="p-6">
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    <i className="fab fa-github" style={{ marginRight: '8px', color: '#000000' }}></i>
                    GitHub Integration
                </h2>
                <p className="text-gray-600">
                    Manage repositories and access tokens
                </p>
            </div>

            <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

            {activeTab === 'repositories' && (
                <div className="mt-6">
                    {error && (
                        <div className="mb-6">
                            <Alert variant="error" dismissible onDismiss={() => setError(null)}>
                                {error}
                            </Alert>
                        </div>
                    )}

                    {success && (
                        <div className="mb-6">
                            <Alert variant="success" dismissible onDismiss={() => setSuccess(null)}>
                                {success}
                            </Alert>
                        </div>
                    )}

                    {loading ? (
                <div className="flex justify-center items-center p-12">
                    <Loader size="lg" text="Loading repositories..." />
                </div>
            ) : repositories.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                    <i className="fas fa-folder-open" style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '16px' }}></i>
                    <p className="text-gray-600">No repositories found</p>
                    <p className="text-sm text-gray-500 mt-2">
                        Make sure your GitHub token is configured correctly
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b">
                        <h3 className="text-lg font-semibold">
                            <i className="fas fa-folder" style={{ marginRight: '8px', color: '#000000' }}></i>
                            Repositories ({repositories.length})
                        </h3>
                    </div>
                    <div className="divide-y divide-gray-200">
                        {repositories.map((repo) => {
                            const assignedProject = getAssignedProject(repo.full_name);

                            return (
                                <div key={repo.id} className="p-6 hover:bg-gray-50">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center mb-2">
                                                <ProjectTypeIcon language={repo.language} />
                                                <a
                                                    href={repo.html_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-medium text-lg text-[#FFC65D] hover:underline"
                                                >
                                                    {repo.name}
                                                </a>
                                                {repo.private && (
                                                    <Badge variant="warning" className="ml-2">Private</Badge>
                                                )}
                                                {assignedProject && (
                                                    <Badge variant="success" className="ml-2">
                                                        Assigned to: {assignedProject.name}
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-600 mb-3">
                                                {repo.description || 'No description'}
                                            </p>
                                            <div className="flex items-center text-xs text-gray-500 space-x-4">
                                                <span>
                                                    <i className="fas fa-code" style={{ marginRight: '4px' }}></i>
                                                    {repo.language || 'N/A'}
                                                </span>
                                                <span>
                                                    <i className="far fa-calendar" style={{ marginRight: '4px' }}></i>
                                                    Updated {new Date(repo.updated_at).toLocaleDateString()}
                                                </span>
                                                <span>
                                                    <i className="fas fa-star" style={{ marginRight: '4px' }}></i>
                                                    {repo.stargazers_count} stars
                                                </span>
                                            </div>
                                        </div>

                                        <div className="ml-6 flex items-center space-x-3">
                                            <div style={{ minWidth: '200px' }}>
                                                <Select
                                                    value={selectedProjects[repo.full_name] || ''}
                                                    onChange={(e) => setSelectedProjects({
                                                        ...selectedProjects,
                                                        [repo.full_name]: e.target.value
                                                    })}
                                                    options={[
                                                        { value: '', label: 'Select project...' },
                                                        ...projects.map(p => ({
                                                            value: p.id.toString(),
                                                            label: p.name
                                                        }))
                                                    ]}
                                                    disabled={!!assignedProject}
                                                    fullWidth
                                                />
                                            </div>
                                            <Button
                                                onClick={() => assignToProject(repo.full_name)}
                                                disabled={!selectedProjects[repo.full_name] || assigning[repo.full_name] || !!assignedProject}
                                                variant="primary"
                                                size="sm"
                                            >
                                                {assigning[repo.full_name] ? (
                                                    <>
                                                        <Loader size="xs" inline /> Assigning...
                                                    </>
                                                ) : assignedProject ? (
                                                    'Assigned'
                                                ) : (
                                                    'Assign'
                                                )}
                                            </Button>
                                            <Button
                                                onClick={() => setDeleteModal({ show: true, repo })}
                                                variant="danger"
                                                size="sm"
                                            >
                                                <i className="fas fa-trash" style={{ marginRight: '4px' }}></i>
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.show && deleteModal.repo && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center mb-4">
                            <i className="fas fa-exclamation-triangle text-red-500" style={{ fontSize: '24px', marginRight: '12px' }}></i>
                            <h3 className="text-xl font-bold text-gray-900">Delete Repository</h3>
                        </div>

                        <div className="mb-4">
                            <p className="text-gray-700 mb-2">
                                Are you sure you want to delete <strong>{deleteModal.repo.full_name}</strong>?
                            </p>
                            <p className="text-sm text-red-600 mb-4">
                                <i className="fas fa-warning" style={{ marginRight: '4px' }}></i>
                                This action is permanent and cannot be undone. The repository will be deleted from GitHub.
                            </p>

                            {error && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                                    <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                                    {error}
                                </div>
                            )}

                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type DELETE to confirm:
                            </label>
                            <input
                                type="text"
                                value={deleteConfirmation}
                                onChange={(e) => setDeleteConfirmation(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                placeholder="DELETE"
                            />
                        </div>

                        <div className="flex justify-end space-x-3">
                            <Button
                                onClick={() => {
                                    setDeleteModal({ show: false, repo: null });
                                    setDeleteConfirmation('');
                                    setError(null);
                                }}
                                variant="secondary"
                                size="sm"
                                disabled={deleting}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={deleteRepository}
                                variant="danger"
                                size="sm"
                                disabled={deleteConfirmation !== 'DELETE' || deleting}
                            >
                                {deleting ? (
                                    <>
                                        <Loader size="xs" inline /> Deleting...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-trash" style={{ marginRight: '4px' }}></i>
                                        Delete Repository
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
                </div>
            )}

            {activeTab === 'tokens' && (
                <div className="mt-6">
                    <GitHubTokenManager service={service} isOnline={isOnline} />
                </div>
            )}
        </div>
    );
};

export default GitHubBrowser;
